import React, { useState, useEffect } from 'react';
import './Profile.css';
import defaultImage from "../Profile/profile.png";
import Address from "./Address";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaSave, FaUser, FaEnvelope, FaPhone, FaCamera, FaTrash, FaTimes, FaCheck } from "react-icons/fa";

const Profile = () => {
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);


    const [selectedImageBase64, setSelectedImageBase64] = useState(null);



    // Load image from localStorage FIRST
    const [userDetails, setUserDetails] = useState({
        userName: "",
        emailId: "",
        mobileNumber: "",
        image: localStorage.getItem("profileImage") || "",
    });

    // Detect Mobile
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Fetch Profile API
    useEffect(() => {
        const userId = localStorage.getItem("userId");
        const token = localStorage.getItem("authToken");

        if (!userId) {
            setError("User not logged in.");
            setLoading(false);
            return;
        }

        const apiUrl = `https://backend.maamaas.com/subscription/api/user/getprofile/${userId}`;

        const fetchProfile = async () => {
            try {
                const response = await fetch(apiUrl, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                });

                if (!response.ok) throw new Error("Failed to fetch profile");
                const data = await response.json();

                // Save API Base64 image into localStorage
                if (data.image) {
                    localStorage.setItem("profileImage", data.image);
                }

                setUserDetails({
                    userName: data.userName || "",
                    emailId: data.emailId || "",
                    mobileNumber: data.mobileNumber || "",
                    image: data.image || localStorage.getItem("profileImage") || "",
                });
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, []);

    // Input Change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setUserDetails((prev) => ({ ...prev, [name]: value }));
    };

    // Image Upload
    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onloadend = () => {
            const base64 = reader.result;
            // Update UI instantly
            setUserDetails((prev) => ({ ...prev, image: base64 }));
            // Save to localStorage
            localStorage.setItem("profileImage", base64);
            // Update server
            updateProfile(base64);
        };
        reader.readAsDataURL(file);
    };

    // PUT API Update
    const updateProfile = async (imageBase64) => {
        const userId = localStorage.getItem("userId");
        const token = localStorage.getItem("authToken");

        const payload = {
            userName: userDetails.userName,
            emailId: userDetails.emailId,
            mobileNumber: userDetails.mobileNumber,
            image: imageBase64 || userDetails.image,
        };

        const response = await fetch(
            `https://backend.maamaas.com/subscription/api/user/editprofile/${userId}`,
            {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            }
        );

        if (!response.ok) {
            throw new Error("Profile update failed");
        }

        return response.json();
    };



    const handleSaveClick = async () => {
        setIsSaving(true);
        setError("");

        try {
            await updateProfile(selectedImageBase64); // 🔥 API CALL
            setIsEditing(false); // edit mode OFF
        } catch (err) {
            setError("Failed to save profile");
        } finally {
            setIsSaving(false);
        }
    };


    // Delete Account
    const handleDeleteAccount = async () => {
        const userId = localStorage.getItem("userId");
        const token = localStorage.getItem("authToken");

        if (!userId) {
            setError("❌ No userId found");
            return;
        }

        try {
            const response = await fetch(
                `https://backend.maamaas.com/subscription/api/user/delete/account/${userId}`,
                {
                    method: "DELETE",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: token ? `Bearer ${token}` : "",
                    },
                }
            );

            //console.log("📌 Delete Status:", response.status);

            if (response.ok) {
                alert("✔ Account deleted successfully");
                localStorage.clear();
                navigate("/");
            } else {
                const errMsg = await response.text();
                throw new Error(errMsg || "Failed to delete account");
            }
        } catch (err) {
            ////console.log("❌ Error:", err);
            setError(err.message);
        }
    };

    // FINAL Image Source (localStorage or API)
    const getImageSource = () => {
        const img = userDetails.image || localStorage.getItem("profileImage");
        if (!img) return defaultImage;
        return img;
    };

    // Inline styles for mobile view
    const mobileStyles = {
        container: {
            padding: '10px',
            width: '100%',
            maxWidth: '100%',
            margin: '0 auto'
        },
        profileHeader: {
            background: 'linear-gradient(135deg, #ee6521 0%, #ee6521 100%)',
            padding: '25px 15px 15px',
            textAlign: 'center',
            borderRadius: '15px 15px 0 0',
            position: 'relative',
            overflow: 'hidden'
        },
        imageContainer: {
            position: 'relative',
            width: '120px',
            height: '120px',
            margin: '0 auto 15px'
        },
        profileImage: {
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            objectFit: 'cover',
            border: '4px solid white',
            boxShadow: '0 5px 15px rgba(0, 0, 0, 0.2)'
        },
        imageUploadBtn: {
            position: 'absolute',
            bottom: '0',
            right: '0',
            background: '#ee6521',
            color: 'white',
            width: '35px',
            height: '35px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            border: '2px solid white',
            zIndex: 10
        },
        headerContent: {
            marginTop: '10px'
        },
        sectionTitle: {
            color: 'white',
            fontSize: '18px',
            fontWeight: '600',
            marginBottom: '10px',
            textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)'
        },
        actionButtons: {
            display: 'flex',
            justifyContent: 'center',
            gap: '12px',
            marginTop: '8px'
        },
        actionButton: {
            background: 'rgba(255, 255, 255, 0.15)',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
        },
        editButton: {
            background: '#4CAF50',
            borderColor: '#4CAF50'
        },
        saveButton: {
            background: '#2196F3',
            borderColor: '#2196F3'
        },
        deleteButton: {
            background: '#ff4757',
            borderColor: '#ff4757'
        },
        formSection: {
            padding: '20px 15px'
        },
        formGroup: {
            display: 'flex',
            alignItems: 'center',
            background: '#f8f9fa',
            borderRadius: '10px',
            padding: '0 15px',
            marginBottom: '15px',
            border: '1px solid #e1e8ed'
        },
        inputIcon: {
            color: '#ee6521',
            marginRight: '12px',
            fontSize: '16px',
            minWidth: '18px'
        },
        formControl: {
            flex: 1,
            padding: '14px 0',
            border: 'none',
            background: 'transparent',
            fontSize: '14px',
            color: '#2c3e50',
            outline: 'none',
            fontFamily: "'Segoe UI', sans-serif"
        },
        disabledInput: {
            color: '#7f8c8d',
            background: 'transparent'
        },
        modalOverlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '15px'
        },
        modalContent: {
            background: 'white',
            borderRadius: '15px',
            width: '100%',
            maxWidth: '350px',
            overflow: 'hidden',
            animation: 'modalSlideIn 0.3s ease'
        },
        '@keyframes modalSlideIn': {
            from: {
                opacity: 0,
                transform: 'translateY(-30px) scale(0.95)'
            },
            to: {
                opacity: 1,
                transform: 'translateY(0) scale(1)'
            }
        },
        modalHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px',
            background: 'linear-gradient(135deg, #ff4757 0%, #ff3838 100%)',
            color: 'white'
        },
        modalBody: {
            padding: '25px 20px',
            textAlign: 'center',
        },
        warningIcon: {
            width: '60px',
            height: '60px',
            background: '#ffebee',
            color: '#ff4757',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            margin: '0 auto 20px'
        },
        warningText: {
            color: '#666',
            fontSize: '14px',
            lineHeight: '1.5',
            marginBottom: '10px'
        },
        modalActions: {
            display: 'flex',
            gap: '10px',
            padding: '20px',
            borderTop: '1px solid #eee'
        },
        cancelBtn: {
            flex: 1,
            background: '#6c757d',
            color: 'white',
            border: 'none',
            padding: '12px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
        },
        confirmDeleteBtn: {
            flex: 1,
            background: 'linear-gradient(135deg, #ff4757 0%, #ff3838 100%)',
            color: 'white',
            border: 'none',
            padding: '12px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
        }
    };




    return (
        <div className="profile-container">
            <div className="container">
                {/* Mobile View */}
                {isMobile ? (
                    <div style={mobileStyles.container}>
                        <div className="profile-card">
                            <div style={mobileStyles.profileHeader}>
                                <h3 style={mobileStyles.sectionTitle}>Profesanl Information</h3>
                                <div style={mobileStyles.imageContainer}>
                                    <img
                                        src={getImageSource()}
                                        alt="Profile"
                                        style={mobileStyles.profileImage}
                                    />
                                    <label htmlFor="imageUpload" style={mobileStyles.imageUploadBtn}>
                                        <FaCamera />
                                    </label>
                                    <input
                                        type="file"
                                        id="imageUpload"
                                        accept="image/*"
                                        style={{ display: "none" }}
                                        onChange={handleImageChange}
                                    />
                                </div>

                                <div style={mobileStyles.headerContent}>
                                    {/* <h3 style={mobileStyles.sectionTitle}>Personal Information</h3> */}
                                    <div style={mobileStyles.actionButtons}>
                                        {!isEditing ? (
                                            <button
                                                style={{ ...mobileStyles.actionButton, ...mobileStyles.editButton }}
                                                onClick={() => setIsEditing(true)}
                                                title="Edit Profile"
                                            >
                                                <FaEdit size={16} />
                                            </button>
                                        ) : (
                                            <button
                                                style={{ ...mobileStyles.actionButton, ...mobileStyles.saveButton }}
                                                onClick={handleSaveClick}
                                                disabled={isSaving}
                                                title="Save Changes"
                                            >
                                                <FaSave size={16} />
                                            </button>
                                        )}
                                        <button
                                            style={{ ...mobileStyles.actionButton, ...mobileStyles.deleteButton }}
                                            onClick={() => setShowDeleteConfirm(true)}
                                            title="Delete Account"
                                        >
                                            <FaTrash size={16} />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div style={mobileStyles.formSection}>
                                <div style={mobileStyles.formGroup}>
                                    <div style={mobileStyles.inputIcon}>
                                        <FaUser />
                                    </div>
                                    <input
                                        style={{
                                            ...mobileStyles.formControl,
                                            ...(!isEditing ? mobileStyles.disabledInput : {})
                                        }}
                                        type="text"
                                        name="userName"
                                        value={userDetails.userName}
                                        onChange={handleInputChange}
                                        disabled={!isEditing}
                                        placeholder="Enter Your Name"
                                    />
                                </div>

                                <div style={mobileStyles.formGroup}>
                                    <div style={mobileStyles.inputIcon}>
                                        <FaEnvelope />
                                    </div>
                                    <input
                                        style={{
                                            ...mobileStyles.formControl,
                                            ...(!isEditing ? mobileStyles.disabledInput : {})
                                        }}
                                        type="email"
                                        name="emailId"
                                        value={userDetails.emailId}
                                        onChange={handleInputChange}
                                        disabled={!isEditing}
                                        placeholder="Enter Your Email"
                                    />
                                </div>

                                <div style={mobileStyles.formGroup}>
                                    <div style={mobileStyles.inputIcon}>
                                        <FaPhone />
                                    </div>
                                    <input
                                        style={{
                                            ...mobileStyles.formControl,
                                            ...(!isEditing ? mobileStyles.disabledInput : {})
                                        }}
                                        type="tel"
                                        name="mobileNumber"
                                        value={userDetails.mobileNumber}
                                        onChange={handleInputChange}
                                        disabled={!isEditing}
                                        placeholder="Enter Your Mobile Number"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* <Address /> */}
                    </div>
                ) : (
                    /* Desktop View - Keep original structure */
                    <div className="profile-card">
                        <div className="row">
                            {/* LEFT: Profile Image */}
                            <div className="col-lg-4 col-md-4 col-sm-12 col-12">
                                <div className="profile-image-section">
                                    <div className="profile-image-container">
                                        <img src={getImageSource()} alt="Profile" className="profile-image" />
                                        <label htmlFor="imageUpload" className="image-upload-overlay">
                                            <FaCamera className="camera-icon" />
                                            <span>Change Photo</span>
                                        </label>
                                    </div>
                                    <input
                                        type="file"
                                        id="imageUpload"
                                        accept="image/*"
                                        style={{ display: "none" }}
                                        onChange={handleImageChange}
                                    />
                                </div>
                            </div>

                            {/* RIGHT: Inputs */}
                            <div className="col-lg-8 col-md-8 col-sm-12 col-12">
                                <div className="profile-details-section">
                                    <div className="section-header">
                                        <h3 className="section-title">Personal Information</h3>
                                        <div className="action-buttons">
                                            {/* {!isEditing ? (
    <button
      className="edit-btn-small"
      onClick={() => setIsEditing(true)}
    >
      <FaEdit size={14} />
      <span>Edit</span>
    </button>
  ) : ( */}
                                            {isEditing && (
                                                <button
                                                    className="save-btn-small"
                                                    onClick={handleSaveClick}
                                                    disabled={isSaving}
                                                >
                                                    <FaSave size={14} />
                                                    <span>{isSaving ? "Saving..." : "Save"}</span>
                                                </button>
                                            )}
                                            {/* )} */}

                                            <button
                                                className="delete-btn-small"
                                                onClick={() => setShowDeleteConfirm(true)}
                                            >
                                                <FaTrash size={14} />
                                                <span>Delete Account</span>
                                            </button>
                                        </div>

                                    </div>

                                    <div className="form-group">
                                        <div className="input-icon">
                                            <FaUser />
                                        </div>
                                        <input
                                            className="form-control"
                                            type="text"
                                            name="userName"
                                            value={userDetails.userName}
                                            onChange={handleInputChange}
                                            disabled={!isEditing}
                                            placeholder="Enter Your Name"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <div className="input-icon">
                                            <FaEnvelope />
                                        </div>
                                        <input
                                            className="form-control"
                                            type="email"
                                            name="emailId"
                                            value={userDetails.emailId}
                                            onChange={handleInputChange}
                                            disabled={!isEditing}
                                            placeholder="Enter Your Email"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <div className="input-icon">
                                            <FaPhone />
                                        </div>
                                        <input
                                            className="form-control"
                                            type="tel"
                                            name="mobileNumber"
                                            value={userDetails.mobileNumber}
                                            onChange={handleInputChange}
                                            disabled={!isEditing}
                                            placeholder="Enter Your Mobile Number"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* {!isMobile && <Address />} */}
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div style={mobileStyles.modalOverlay}>
                    <div style={mobileStyles.modalContent}>
                        <div style={mobileStyles.modalHeader}>
                            <h3 style={{ margin: 0, fontSize: '18px' }}>Delete Account</h3>
                            <button
                                style={{
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    border: 'none',
                                    color: 'white',
                                    width: '30px',
                                    height: '30px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer'
                                }}
                                onClick={() => setShowDeleteConfirm(false)}
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <div style={mobileStyles.modalBody}>
                            <div style={mobileStyles.warningIcon}>
                                <FaTrash />
                            </div>
                            <p style={mobileStyles.warningText}>
                                Are you sure you want to delete your account? This action cannot be undone and all your data will be permanently removed.
                            </p>
                        </div>
                        <div style={mobileStyles.modalActions}>
                            <button
                                style={mobileStyles.cancelBtn}
                                onClick={() => setShowDeleteConfirm(false)}
                            >
                                Cancel
                            </button>
                            <button
                                style={mobileStyles.confirmDeleteBtn}
                                onClick={() => {
                                    ////console.log("🛑 Delete button clicked");
                                    handleDeleteAccount();
                                }}
                            >
                                Delete Account
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Profile;